// @ts-strict-ignore
import debounce from 'lodash/debounce';
import findLastIndex from 'lodash/findLastIndex';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import sortedIndex from 'lodash/sortedIndex';
import sortedLastIndex from 'lodash/sortedLastIndex';
import sum from 'lodash/sum';
import {AttrsSchema, Component} from 'panel';
import {DOMPatcher} from 'panel/lib/dom-patcher';
import ResizeObserver from 'resize-observer-polyfill';
import produce from 'immer';

import {reporter} from 'common/report/error-reporting';
import {
  generateSegmentCellBlocks,
  getNumSegments,
  getResizedColIndices,
  getSortSelectSections,
  getSummedColumnWidth,
  getTotalColumnsWidth,
  getViewNLimit,
  invertSortOrder,
  isPostViewNSortConfig,
  isSameRowByContext,
  isSegmentColumn,
  segmentCellBlocksEnabled,
  updateRowsWithSegmentBoundaries,
  updateWidthOverridesOnColumnsChange,
} from 'common/components/data-table/utils';
import {emitTrackEvent, isEllipsisActive} from 'common/util/dom';
import {getModifierKeys} from 'common/util/keyboard';
import {fuzzyMatchSanitized, matchPartsFromSegments} from 'common/util/fuzzy-match';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {truncateMiddle} from 'common/util/string';
import {SortBy, SortByValueConfig, SortConfig, SortOrder} from 'common/types/reports/bookmark';
import {commonTrackingEventPropertyName} from 'common/report/tracking-setup';
import {ProjectFeatureFlag} from 'common/report/constants';
import {IronContextRegistry} from 'common/report/context-registry';
import {parseNumber} from 'common/util';
import {MenuChangeEvent} from 'common/components/select/types';
import {getReportName} from 'common/report/util';

import {
  CssClass,
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_RESIZE_MIN_WIDTH,
  DEFAULT_SEGMENT_COLUMN_WIDTH,
  GAP_BORDER_SIZE,
  MIN_DATA_COLUMNS_VIEWPORT_WIDTH,
  TABLE_HORIZONTAL_PADDING,
  TIME_COMPARISON_HEADER_ROW_HEIGHT,
  TITLE_ROW_HEIGHT,
  TITLE_ROW_WITH_SUBTITLE_HEIGHT,
  TrackingPropertyName,
  VIEW_N_DEFAULT_LIMIT,
} from './constants';
import css from './index.inline.styl';
import stylusConstants from './stylus-constants.json';
import {template} from './template';
import {Themes} from './themes';
import {
  BodyCellRenderContext,
  BodyRowHoverEvent,
  CellClickEvent,
  CellRenderContext,
  CollapseChangeEvent,
  ColSortOptionValue,
  ColumnResizeEvent,
  Columns,
  ColumnWidthOverrides,
  CommonDataTableFields,
  DataColumn,
  DataTableAttrs,
  DataTableJsxProps,
  DataTableParams,
  GroupHeader,
  InitialColResizeState,
  isTimeComparisonColumnType,
  RowType,
  SegmentColumn,
  SelectionChangeEvent,
  SortChangeEvent,
  TableCell,
  TableColumn,
  TableColumnType,
  TableRow,
  TableState,
  Theme,
  TimeComparisonColumnType,
  TruncatableTextType,
} from './types';

const COMPONENT_NAME = `mp-data-table`;
const TRACK_EVENT = {
  LOADED: `Load`,
  SELECTION_CHANGE: `Selection Change`,
  SEARCH: `Search`,
  CLEAR_SEARCH: `Clear Search`,
  TRUNCATED_SEGMENTS_INFO_IMPRESSION: `Truncated Segments Info Impression`,
  RESIZED_COLUMN: `Resized Column`,
  VIEW_N_CHANGE: `View N Change`,
};

const SCROLL_THROTTLE_MS = 100;

const NUM_PADDING_ROWS_TO_RENDER = 16;
const NUM_PADDING_DATA_COLS_TO_RENDER = 8;

/**
 * mp-data-table
 *
 * display mostly numeric data of a repetitive format densely in a scrolling container
 * with a sticky header and sticky columns.
 *
 * Overview doc with test examples: https://www.notion.so/mxpnl/Data-Tables-Product-Testing-Overview-9fd67ad2f2d042d5b94b119ab30b9ca5
 *
 * examples: retention data, funnels step data
 */
export class DataTable extends Component<TableState, DataTableAttrs, EmptyObj, EmptyObj, IronContextRegistry> {
  private declare onResizeMouseMove: (ev: MouseEvent) => void;
  private declare onResizeMouseUp: (ev: MouseEvent) => void;
  private declare onWindowBlur: () => void;
  private _verticalScrolled = false;

  static get attrsSchema(): AttrsSchema<DataTableAttrs> {
    // TODO: Remove dependence on `collapsible` and `multi-select` attrs. They can be computed based on info in
    // rows/columnGroups. Also consider refactoring to remove dependence on `theme` attr.
    return {
      ...super.attrsSchema,
      loading: {
        type: `boolean`,
        description: `Setting this to true (when loading starts) and then false (when loaded) will result in the loading time to be tracked`,
      },
      'multi-select': {
        type: `boolean`,
        description: `Show checkboxes besides each row and allow select all / none of current filtered results`,
      },
      'selection-limit': {
        type: `number`,
        description: `Limit the number of selections allowed. Also disables shift-click.`,
      },
      collapsible: {
        type: `boolean`,
        description: `Allow grouped rows to be collapsible`,
      },
      sorting: {
        type: `json`,
        description: `Used to visually style the columns. Sorted rows should be passed into <mp-data-table> as it does not internally sort`,
        tsType: `SortConfig`,
      },
      'has-comparison': {
        type: `boolean`,
        description: `If comparison rows are interleaved in the rows passed to this component's rows prop`,
      },
      'track-name': {
        type: `string`,
        description: `If specified, dispatches '[mp-data-table] <event>' bubbled events that can be listened for tracking with name as a property`,
        tsType: `TrackName`,
      },
      theme: {
        type: `string`,
        enum: Object.values(Theme),
        default: Theme.Default,
      },
      'column-width-overrides': {
        type: `json`,
        tsType: `ColumnWidthOverrides`,
      },
      'segmentation-limit': {
        type: `number`,
        default: Number.MAX_SAFE_INTEGER,
        description: `See associated attr in <mp-segmentation-limit-info>`,
      },
      'export-segmentation-limit': {
        type: `number`,
        default: Number.MAX_SAFE_INTEGER,
        description: `See associated attr in <mp-segmentation-limit-info>`,
      },
      'disable-scroll-control': {
        type: `boolean`,
        description: `Disables mp-scroll-control`,
      },
      // TODO(mack): Should invert + rename this is-dashboard-mode for clarity and to avoid misuse
      'resizing-layout': {
        type: `boolean`,
        description: `'resizing-layout': false means this is a dashboard card`,
      },
      'selected-cell': {
        type: `json`,
        description: `Coordinates for the focused cell (e.g. with an active popover)`,
        tsType: `CellCoordinates`,
      },
      'start-scrolled-right': `boolean`,
      'is-pivot-view': `boolean`,
      'lift-comparison-type': {
        type: `string`,
        default: null,
        tsType: `LiftType`,
      },
      'hovered-series-id': {
        type: `string`,
        default: null,
        description: `The id / name of the series that should be virtually hovered. Used to highlight a table row when a series is hovered in the chart.`,
      },
    };
  }

  private declare loadingStartTimeMs: number;
  private _selectedRowCount = 0;
  private _truncatedSegmentsInfoObserver: IntersectionObserver = null;
  private _customTrackingProps: Record<string, any> = {};

  private declare domPatcher: DOMPatcher;
  private _resizedColumnCount = 0;
  private declare _oldViewNSortConfig: SortConfig;
  // Promise will be set when View N input is focused. It will be resolved when input is blurred.
  private declare _viewNLimitInputBlurPromise: Promise<void>;
  private declare _resolveViewNLimitInputBlur: () => void;

  private _rawColumnGroups = null;

  /** Used to delay firing of ColumnResizeEvent until parent listeners are active */
  private _deferredColumnWidthOverrides: ColumnWidthOverrides = null;

  constructor() {
    super();
    this.loadingStartTimeMs = Date.now(); // unix milliseconds when loading started
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      contexts: [`currentProject` as const, `reportLayoutManager` as const, `i18n` as const],
      defaultState: {
        rows: [],
        filteredRows: [],
        flatFilteredRows: [],
        rowHeightsMatrix: [],
        dataColumnWidthsMatrix: [],
        columns: [],
        stickyColumnsCount: 0,
        hasBodyScrolled: false,
        renderRowStartIdx: 0,
        numRowsToRender: 0,
        renderDataColumnStartIdx: 0,
        numDataColumnsToRender: 0,
        sorting: null,
        columnWidthOverrides: {},
        tableWidth: null,
        resizeColumnIndex: null,
        hoveredCellContext: null,
        hoveredTruncatedTextType: null,
        isSelectAllHovered: false,
        groupHeaders: null,
        searchFilter: null,
        segmentCellBlocks: [],
        scrollEl: null,
        scrollParentEl: null,
      },
      helpers: {
        truncateMiddle,

        getCellLabel: (row: TableRow<TableCell>, col: TableColumn): string => {
          const label = col.labelTransform ? col.labelTransform(row, col.field) : row[col.field];
          if (label === undefined || label === null) {
            return ``;
          }
          return label.toString();
        },

        getBodyCellRenderContext: (row: TableRow, col: TableColumn): BodyCellRenderContext => {
          const hasSegmentCellBlocks = this.showSegmentCellBlocks();
          const segmentStartCell =
            hasSegmentCellBlocks && isSegmentColumn(col) && row.segmentBlockStarts?.has(col.segmentIndex);
          const segmentEndCell =
            hasSegmentCellBlocks && isSegmentColumn(col) && row.segmentBlockEnds?.has(col.segmentIndex);
          return {
            rowType: RowType.Body,
            field: col.field,
            liftField: col.liftField,
            liftExtra: col.liftExtra,
            label: this.helpers.getCellLabel(row, col),
            isComparison: row.isComparisonRow,
            columnIdx: col.columnIdx,
            rowIdx: row.rowIdx,
            groupedRowIdx: row.groupedRowIdx,
            segmentStartCell,
            segmentEndCell,
          };
        },

        getGapSize: () => {
          return this.getTheme().cellBorders === `gap` ? GAP_BORDER_SIZE : 0;
        },

        getInternalTableWidth: () => {
          const {scrollEl} = this.state;
          if (scrollEl) {
            // Based on https://stackoverflow.com/a/29881817. This approach is more accurate than other codepath
            // because it accounts for width of vertical scrollbar if it exists.
            const computedStyle = window.getComputedStyle(scrollEl);
            return (
              scrollEl.clientWidth - parseFloat(computedStyle.paddingLeft) - parseFloat(computedStyle.paddingRight)
            );
          } else {
            // TODO: Should remove this codepath and instead wait until scrollEl is set before rendering the
            // internals of the data tables.
            // This codepath is hit on initial render before scrollEl has been inserted
            return this.state.tableWidth - 2 * TABLE_HORIZONTAL_PADDING;
          }
        },

        toggleSelected: (ev, row) => {
          ev.stopPropagation(); // so we don't also fire the rowClick event
          document.getSelection().empty(); // so text isn't highlighted on shift click or double click

          if (!row.isSelectDisabled) {
            const isSelected = !row.isSelected;
            // TODO: Change this to return a single entry rather than an array
            const changed = [{isSelected, row}];

            this.dispatchEvent(
              new CustomEvent(`selectionChange`, {
                detail: {
                  changed,
                  isToggleAll: false,
                  searchFilter: !!this.state.searchFilter,
                  rows: this.state.filteredRows,
                },
              }) as SelectionChangeEvent,
            );
            this.helpers.track(TRACK_EVENT.SELECTION_CHANGE, {
              isSelected,
              [TrackingPropertyName.prevNumSelected]: this.selectedRowCount,
              [TrackingPropertyName.numSelected]: this.selectedRowCount + (isSelected ? 1 : -1),
            });
          }
        },

        collapsibleClicked: (ev, row: TableRow) => {
          ev.stopPropagation();
          const isCollapsed = !row.isCollapsed;

          // Internally update the collapse state so it's re-rendered right away. The `collapseChange` event will also
          // result in new rows being passed in that have the updated collapsed state, but waiting for that to happen
          // results in the collapse/expand interaction feeling laggy.
          row.isCollapsed = isCollapsed;
          this.updateFlatFilteredRows();

          this.dispatchEvent(
            new CustomEvent(`collapseChange`, {
              detail: {
                isCollapsed,
                row,
              },
            }) as CollapseChangeEvent,
          );
        },

        getGlobalSelectionStats: () => {
          const numSelectableRows = this.state.rows.reduce((acc, row) => acc + (row.isSelectDisabled ? 0 : 1), 0);
          const numSelectable = this.attr(`has-comparison`) ? numSelectableRows / 2 : numSelectableRows;
          const selectionLimit = Math.min(this.attr(`selection-limit`) || Number.POSITIVE_INFINITY, numSelectable);

          return {
            numSelected: this.selectedRowCount,
            numSelectable,
            selectionLimit,
            atSelectionLimit: this.selectedRowCount >= selectionLimit || this._allFilteredRowsSelected(),
            hasReachedSegmentationLimit: numSelectable >= this.attr(`segmentation-limit`),
          };
        },

        getCheckboxState: ({row, atSelectionLimit}: {row: TableRow<TableCell>; atSelectionLimit: boolean}) => {
          if (row.isSelected) {
            const options: any = {
              attrs: {
                checked: true,
                disabled: false,
              },
            };
            if (row.checkedColor) {
              options.attrs[`checked-color`] = row.checkedColor;
            }
            return options;
          }

          if (atSelectionLimit) {
            return {
              selectDisabledReason:
                row.selectDisabledReason || `You can't select more than ${this.attr(`selection-limit`)} items.`,
              attrs: {disabled: true, checked: false},
            };
          }

          return {
            selectDisabledReason: row.selectDisabledReason,
            attrs: {disabled: row.isSelectDisabled, checked: false},
          };
        },

        getCellItem: (row: TableRow<TableCell>, columnIdx: number) => {
          let cell;
          if (row.cells) {
            const cellColumnIdx = columnIdx - this.state.stickyColumnsCount;
            cell = cellColumnIdx < row.cells.length && cellColumnIdx >= 0 ? row.cells[cellColumnIdx] : null;
          }
          return cell;
        },

        toggleAllSelected: () => {
          this.dispatchEvent(
            new CustomEvent(`selectionChange`, {
              detail: {
                isToggleAll: true,
                rows: this.state.filteredRows,
                isSearchMode: !!this.state.searchFilter,
              },
            }) as SelectionChangeEvent,
          );
          const {atSelectionLimit, selectionLimit} = this.helpers.getGlobalSelectionStats();
          this.helpers.track(TRACK_EVENT.SELECTION_CHANGE, {
            isSelectAll: !atSelectionLimit,
            toggleAll: true,
            [TrackingPropertyName.prevNumSelected]: this.selectedRowCount,
            [TrackingPropertyName.numSelected]: atSelectionLimit ? 0 : selectionLimit,
          });
        },

        handleHeaderTitleClick: (column: TableColumn) => {
          if (isSegmentColumn(column) || !column.isSortable) {
            return;
          }

          // flat sort for non-segment column

          const {sorting} = this.state;
          let newSortOrder: SortOrder;
          if (sorting.sortBy === SortBy.Value && sorting.valueField === column.field) {
            newSortOrder = invertSortOrder(sorting.sortOrder);
          } else {
            // sort column (valueField) changed
            newSortOrder = SortOrder.Descending;
          }
          const newSorting: SortByValueConfig = {
            sortBy: SortBy.Value,
            sortOrder: newSortOrder,
            valueField: column.field,
            colSortAttrs: sorting.colSortAttrs,
          };
          this.update({sorting: newSorting});
          this._emitSortChange();
        },

        handleSortSelect: (ev) => {
          const {segmentIndex, sort} = ev.detail.item.value as ColSortOptionValue;
          const {sorting} = this.state;
          sorting.colSortAttrs[segmentIndex] = sort;
          this.update({sorting: {sortBy: SortBy.Column, colSortAttrs: sorting.colSortAttrs}});
          this._emitSortChange();
        },

        handleCellClick: (
          ev: MouseEvent,
          row: TableRow<TableCell>,
          field: string,
          context: BodyCellRenderContext,
          {
            bgColor,
            textColor,
          }: {
            bgColor: string;
            textColor: string;
          },
        ) => {
          this.dispatchEvent(
            new CustomEvent(`cellClick`, {
              detail: {
                row,
                field,
                context,
                target: ev.currentTarget,
                modifierKeys: getModifierKeys(ev),
                bgColor,
                textColor,
                mouseX: ev.clientX,
                mouseY: ev.clientY,
                column: this.state.columns[context.columnIdx],
              },
            }) as CellClickEvent,
          );
        },

        handleCellPointerEnter: (context: CellRenderContext) => {
          if (Number.isInteger(this.state.resizeColumnIndex)) {
            // prevent e.g. showing tooltips (which change the cell's z-index) when resizing a column
            return;
          }

          const prevContext = this.state.hoveredCellContext;

          this.update({hoveredCellContext: context});
          this.helpers.fireRowHoverEvent({context, prevContext});
        },

        handleCellPointerLeave: (ev) => {
          if (
            Number.isInteger(this.state.resizeColumnIndex) ||
            // When moving between columns of the same row, you'll pass through the resize handle. In this case, we
            // should still consider the row hovered. Otherwise, the background color will flicker as the row switches
            // between hovered/non-hovered styling.
            (ev.relatedTarget?.classList.contains(CssClass.ColumnResizeHandle) &&
              this.state.hoveredCellContext?.rowType !== RowType.Header) ||
            // Prevent flicker when cursor is between rows in retention, which has grid gap
            ev.relatedTarget?.classList.contains(CssClass.ScrollableColumns) ||
            ev.relatedTarget?.classList.contains(CssClass.FixedColumns) ||
            // update will be handled by the next cell's handleCellPointerEnter
            ev.relatedTarget?.classList.contains(`mp-table-cell`)
          ) {
            return;
          }

          this.cellPointerLeaveActions();
        },

        handleTruncatableTextPointerEnter: (
          ev,
          truncatableTextType: TruncatableTextType = TruncatableTextType.Default,
        ) => {
          const textEl = ev.currentTarget;
          this.update({
            hoveredTruncatedTextType: Boolean(textEl) && isEllipsisActive(textEl) ? truncatableTextType : null,
          });
        },

        handleTruncatableTextPointerLeave: () => {
          this.update({
            hoveredTruncatedTextType: null,
          });
        },

        handleSegmentBlockPointerEnter: () => {
          // Ensures the cellPointerLeaveActions are fired when the pointer moves from a cell to the segment block
          // through a column resize handle
          this.cellPointerLeaveActions();
        },

        truncatedSegmentsBannerEnabled: () => {
          // Default to true since this is the old currently rolled out behavior.
          try {
            const currentProject = this.getContext(`currentProject`)?.get()?.value;
            if (!currentProject) {
              return true;
            }
            // Hide the old truncated segments banner if the new one is enabled.
            return !currentProject.hasFeatureFlag(ProjectFeatureFlag.BetterErrors);
          } catch {
            return true;
          }
        },

        fireRowHoverEvent: ({
          context,
          prevContext,
        }: {
          context: CellRenderContext | null;
          prevContext: CellRenderContext | null;
        }) => {
          if ((context as BodyCellRenderContext)?.rowIdx === (prevContext as BodyCellRenderContext)?.rowIdx) {
            return;
          }

          let rowId: string;
          if (context?.rowType === RowType.Body) {
            rowId = this.state.rows[context.rowIdx].identifier;
          } else {
            rowId = null;
          }
          this.dispatchEvent(new CustomEvent(`bodyRowHover`, {detail: {rowId}}) as BodyRowHoverEvent);
        },

        handleSelectAllPointerEnter: () => {
          this.update({isSelectAllHovered: true});
        },

        handleSelectAllPointerLeave: () => {
          this.update({isSelectAllHovered: false});
        },

        handleScroll: debounce(
          () => {
            this.updateVirtualizedRenderStateOnScroll();

            // dispatch a single scroll event to invalidate any positioning of popovers
            this.dispatchEvent(new CustomEvent(`tableScroll`));

            const focused = this.el.querySelectorAll(`:not(.${CssClass.SearchInput}):focus`) || [];
            focused.forEach((f) => f.blur());
          },
          SCROLL_THROTTLE_MS,
          {leading: true},
        ),

        handleSearchTextUpdate: debounce((value: string) => {
          this.update({searchFilter: value});
          this.updateFilteredRows();
          if (value) {
            this.helpers.track(TRACK_EVENT.SEARCH, {searchFilterLength: value.length});
          } else {
            this.helpers.track(TRACK_EVENT.CLEAR_SEARCH);
          }
        }, 250),

        getVerticalScrollEl: () => this.state.scrollEl,

        onInsertTruncatedSegmentsInfo: (el: HTMLElement) => {
          this._truncatedSegmentsInfoObserver = new IntersectionObserver(
            (entries) => {
              if (entries[0].isIntersecting) {
                this.helpers.track(TRACK_EVENT.TRUNCATED_SEGMENTS_INFO_IMPRESSION);
              }
            },
            {root: this.helpers.getVerticalScrollEl()},
          );
          this._truncatedSegmentsInfoObserver.observe(el);
        },

        onDestroyTruncatedSegmentsInfo: () => {
          this._truncatedSegmentsInfoObserver.disconnect();
          this._truncatedSegmentsInfoObserver = null;
        },

        /** Only use sticky columns when the screen width has enough space for them. */
        shouldUseStickyColumns: (): boolean => {
          const columnsToRender = this.getHeaderDataColumnsToRender();
          const dataColumnsWidth = getSummedColumnWidth(columnsToRender);
          const internalTableWidth = this.helpers.getInternalTableWidth();
          const fixedColumns = this.getFixedColumns();
          const fixedColumnsWidth = getSummedColumnWidth(fixedColumns);
          return internalTableWidth - fixedColumnsWidth >= Math.min(MIN_DATA_COLUMNS_VIEWPORT_WIDTH, dataColumnsWidth);
        },

        track: (eventName, props = {}, {eventPrefix = COMPONENT_NAME} = {}) => {
          if (this.attr(`track-name`)) {
            emitTrackEvent(this, {
              eventName,
              eventPrefix,
              props: {
                name: this.attr(`track-name`),
                tab: getReportName(),
                hasReachedSegmentationLimit: this.helpers.getGlobalSelectionStats().hasReachedSegmentationLimit,
                hasSearchFilter: Boolean(this.state.searchFilter),
                numFilteredRows: this.state.filteredRows.length,
                numRows: this.state.rows?.length,
                ...this.getVirtualizedRenderState(),
                [TrackingPropertyName.numSelected]: this.selectedRowCount,
                [commonTrackingEventPropertyName.sortConfig]: this.state.sorting,
                isBoardCard: this.isBoardMode(),
                // passed in props should be after common props above so they can override as needed
                ...props,
                ...this._customTrackingProps,
              },
            });
          }
        },

        trackLoaded: () => {
          this.helpers.track(TRACK_EVENT.LOADED, {
            durationMs: Date.now() - this.loadingStartTimeMs,
          });
        },
      },
    };
  }

  getSortSelectSections(column: SegmentColumn) {
    return getSortSelectSections({
      column,
      columns: this.state.columns,
      sortConfig: this.state.sorting,
    });
  }

  initWidthObserver() {
    const updateWidth = debounce(
      () => {
        if (this.clientWidth !== this.state.tableWidth) {
          this.updateTableWidth(this.clientWidth);
        }
      },
      500,
      {leading: true, trailing: true},
    );
    const widthObserver = new ResizeObserver(updateWidth);
    widthObserver.observe(this);

    this.onDisconnected(() => {
      widthObserver.disconnect();
    });
  }

  public connectedCallback() {
    this.updateTableWidth(this.clientWidth);
    super.connectedCallback();

    // This patching logic is ported over from iron/common/query-builder/query-block/properties-menu/index.ts
    const _render = this.domPatcher.render;
    this.domPatcher.render = () => {
      if (!this.domPatcher) {
        // This function is called in a RAF callback if async patching is set. Check if the component has been
        // disconnected before proceeding.
        return;
      }

      _render.apply(this.domPatcher);
    };

    this.updateVirtualizedRenderState();

    this.initWidthObserver();

    requestAnimationFrame(() => {
      if (!this.attr(`loading`)) {
        this.helpers.trackLoaded();
      }
    });
  }

  public disconnectedCallback() {
    this.cleanResizeEventListeners();

    super.disconnectedCallback();
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `sorting`) {
      const sorting = this.attr(attr);
      this.update({sorting});
    } else if (attr === `loading`) {
      if (this.attr(attr)) {
        this.loadingStartTimeMs = Date.now();
      } else {
        this.helpers.trackLoaded();
      }
    } else if (attr === `column-width-overrides`) {
      // Note: this is currently only needed on first mount to persist column sizes across query updates (which
      // re-render the table)
      const columnWidthOverrides = this.attr(`column-width-overrides`);
      if (!isEqual(this.state.columnWidthOverrides, columnWidthOverrides)) {
        this.update({columnWidthOverrides});
        this.updateComputedColumnWidths();
      }
    }
  }

  public getTheme() {
    return Themes[this.attr(`theme`)];
  }

  isMultimetricMode() {
    return this.attr(`is-pivot-view`);
  }

  showMultimetricFooter() {
    return this.isMultimetricMode() && getNumSegments(this.state.columns) > 0;
  }

  updateRows(val: Array<TableRow<TableCell>>) {
    val.forEach((row, rowIdx) => {
      row.rowIdx = rowIdx;
      row.isComparisonRow = this.attr(`has-comparison`) && rowIdx % 2 === 1;
      if (row.groupedRows) {
        row.groupedRows.forEach((groupedRow, groupedRowIdx) => {
          groupedRow.rowIdx = rowIdx;
          groupedRow.groupedRowIdx = groupedRowIdx;
        });
      }
    });

    this.update({rows: val});
  }

  get selectedRowCount() {
    return this._selectedRowCount;
  }

  set dataTableParams({
    rows = [],
    columnGroups = [],
    segmentMetadata,
    selectedRowCount,
    customTrackingProps,
  }: DataTableParams) {
    if (!isNil(selectedRowCount)) {
      this._selectedRowCount = selectedRowCount;
    }
    if (!isNil(customTrackingProps)) {
      this._customTrackingProps = customTrackingProps;
    }

    this.update({segmentMetadata});
    this.updateColumnGroups(columnGroups);
    this.updateRows(rows);

    this.updateSegmentCellBlocksAndRows();
    this.updateFilteredRows();
    this.updateSegmentCardinalities();
  }

  updateColumnGroups(groups: Columns) {
    if (isEqual(groups, this._rawColumnGroups)) {
      return;
    }
    this._rawColumnGroups = groups;

    const rawGroupHeaders: GroupHeader[] = [];
    const theme = this.getTheme();

    let flatColumns: TableColumn[] = groups
      .map((group, groupIndex, allGroups) => {
        let columns: TableColumn[] = [];

        // Handle ColumnGroup and create a group header for it
        if (`columns` in group) {
          columns = columns.concat(group.columns);
          if (this.countStickyColumns(group.columns) === 0) {
            rawGroupHeaders.push({
              title: group.title,
              funnelStepNumber: group.funnelStepNumber,
              span: group.columns.length,
            });
          }
        } else {
          columns = columns.concat([group]);
        }

        return columns.map((col, colIndex, allCols) => {
          const lastGroup = groupIndex === allGroups.length - 1;
          const first = colIndex === 0;
          const last = colIndex === allCols.length - 1;
          const leftmost = first && groupIndex === 0;
          const rightmost = last && lastGroup;

          return {
            ...col,
            // Sticky columns should be resizable by default
            resizable: col.resizable ?? col.sticky,
            initialWidth:
              col.initialWidth ??
              (col.type === TableColumnType.Segment ? DEFAULT_SEGMENT_COLUMN_WIDTH : DEFAULT_COLUMN_WIDTH),
            first,
            last,
            leftmost,
            rightmost,
          };
        });
      })
      .flat();

    const lastStickyColumnIndex = findLastIndex(flatColumns, (col) => col.sticky);
    flatColumns = flatColumns.map((col, colIndex) => {
      let segmentIndex;
      if (isSegmentColumn(col)) {
        if (!col.field.startsWith(CommonDataTableFields.SegmentPrefix)) {
          reporter.error(`mp-data-table: segment field name must start with expected prefix`, {column: col});
        }
        segmentIndex = parseInt(col.field.slice(CommonDataTableFields.SegmentPrefix.length));
      }
      return {
        ...col,
        lastSticky: colIndex === lastStickyColumnIndex,
        columnIdx: colIndex,
        subsetColumnIdx: col.sticky ? colIndex : colIndex - (lastStickyColumnIndex + 1),
        ...(Number.isInteger(segmentIndex) ? {segmentIndex} : {}),
      };
    });

    const lastSegmentIndex = getNumSegments(flatColumns) - 1;
    const lastSegmentColIndex = flatColumns.findIndex(
      (col) => isSegmentColumn(col) && col.segmentIndex === lastSegmentIndex,
    );
    if (lastSegmentColIndex !== -1) {
      const lastSegmentCol = flatColumns[lastSegmentColIndex] as SegmentColumn;
      lastSegmentCol.lastSegment = true;
      lastSegmentCol.hasCheckbox = theme.selectableRows;
      lastSegmentCol.hasCollapseButton = this.attr(`collapsible`);
    }

    // Note: we can't see the old columns here (this.state.columns is always []) because the table is re-rendered
    // when columnGroups changes
    const newWidthOverrides = updateWidthOverridesOnColumnsChange({
      newColumns: flatColumns,
      oldOverrides: this.state.columnWidthOverrides,
    });
    if (newWidthOverrides) {
      this._deferredColumnWidthOverrides = newWidthOverrides;
    }
    const groupHeaders = rawGroupHeaders.length && rawGroupHeaders.every(({title}) => !!title) ? rawGroupHeaders : null;

    this.update({
      columns: flatColumns,
      stickyColumnsCount: this.countStickyColumns(flatColumns),
      groupHeaders,
      ...(newWidthOverrides ? {columnWidthOverrides: newWidthOverrides} : {}),
    });
    this.updateComputedColumnWidths();
  }

  updateSegmentCardinalities() {
    this.state.columns.forEach((column) => {
      if (!isSegmentColumn(column)) {
        return;
      }
      const segmentValues: Set<string> = new Set();
      for (const row of this.state.rows) {
        segmentValues.add(this.helpers.getCellLabel(row, column));
      }
      column.segmentCardinality = segmentValues.size;
    });
  }

  /** Set up column drag resizing and double click to reset.
   *
   * Note we can't use pointerdown because we need mousedown's ev.detail (number of clicks) to
   * detect double clicks. Alternatively we could listen to the dblclick event separately */
  onResizeMouseDown({ev, resizeColumnIndex}: {ev: MouseEvent; resizeColumnIndex: number}) {
    ev.preventDefault();
    if (ev.detail > 1) {
      // double click
      return this.setDefaultColumnWidths();
    }

    this.update({
      resizeColumnIndex,
    });
    this.cellPointerLeaveActions();

    const initial = this.getInitialColResizeState({ev, resizeColumnIndex});
    this.onResizeMouseMove = (resizeMouseMoveEvent) => this.handleResizeMouseMove({ev: resizeMouseMoveEvent, initial});
    this.onResizeMouseUp = (resizeMouseUpEvent) => this.handleResizeMouseUp({ev: resizeMouseUpEvent, initial});
    this.onWindowBlur = () => this.resetResizeState();

    window.addEventListener(`mousemove`, this.onResizeMouseMove, true);
    window.addEventListener(`mouseup`, this.onResizeMouseUp, true);
    window.addEventListener(`blur`, this.onWindowBlur, true);
  }

  handleResizeMouseMove({ev, initial}: {ev: MouseEvent; initial: InitialColResizeState}) {
    ev.stopPropagation();
    const delta = ev.screenX - initial.x;
    const clampedDelta = Math.max(initial.leftCol.deltaMin, delta);
    // outside of panel render for performance
    initial.handle.el.style.left = `${initial.handle.left + clampedDelta}px`;
  }

  handleResizeMouseUp({ev, initial}: {ev: MouseEvent; initial: InitialColResizeState}) {
    const delta = ev.screenX - initial.x;
    // prevent some weird sub-pixel movements on single click
    if (Math.abs(delta) >= 1) {
      const clampedDelta = Math.max(initial.leftCol.deltaMin, delta);
      const columnWidthOverrides = {...this.state.columnWidthOverrides};
      columnWidthOverrides[initial.leftCol.idx] = initial.leftCol.col.computedWidth + clampedDelta;

      const rightCol = initial.rightCol.col;
      const rightColIdx = initial.rightCol.idx;
      // only sticky columns can be manually resized currently
      if (rightCol && rightCol.resizable && rightCol.sticky) {
        const totalColumnsWidth = getSummedColumnWidth(this.state.columns);
        const tableWidth = this.helpers.getInternalTableWidth();
        if (totalColumnsWidth > tableWidth) {
          // Columns overflow:
          // 1) if leftCol expanding, don't touch right col
          if (clampedDelta < 0) {
            // 2) leftCol is contracting: don't change right column until all columns fit, then
            //    expand it as needed to fill table
            const deltaAfterColumnsFit = -1 * clampedDelta - (totalColumnsWidth - tableWidth);
            if (deltaAfterColumnsFit > 0) {
              columnWidthOverrides[rightColIdx] = rightCol.computedWidth + deltaAfterColumnsFit;
            }
          }
        } else {
          // Columns do not overflow:
          // 1) if leftCol is expanding (clampedDelta is positive), shrink rightCol until it hits
          //    minWidth. Note this may shrink the right col AND make columns overflow
          // 2) if leftCol is shrinking, expand rightCol as much as needed
          columnWidthOverrides[rightColIdx] = Math.max(
            rightCol.computedWidth - clampedDelta,
            initial.rightCol.minWidth,
          );
        }
      }
      this.update({columnWidthOverrides});
      this.updateComputedColumnWidths(true);
      this.dispatchEvent(new CustomEvent(`columnResize`, {detail: {columnWidthOverrides}}) as ColumnResizeEvent);
      this._resizedColumnCount += 1;

      // tracking
      this.helpers.track(TRACK_EVENT.RESIZED_COLUMN, {
        columnType: initial.rightCol.col?.type,
        resizedColumnCount: this._resizedColumnCount,
        columnResizeDeltaPx: delta,
      });
    }
    initial.handle.el.style.left = `${initial.handle.left}px`;
    this.resetResizeState();
  }

  getInitialColResizeState({
    ev,
    resizeColumnIndex,
  }: {
    ev: MouseEvent;
    resizeColumnIndex: number;
  }): InitialColResizeState {
    // cannot use currentTarget to store element reference
    const handleEl = ev.target as HTMLElement;
    const leftCol = this.state.columns[resizeColumnIndex];
    const leftColMinWidth = this.getColumnMinWidth(leftCol);
    const deltaMin = leftColMinWidth - leftCol.computedWidth;
    const rightCol = this.state.columns[resizeColumnIndex + 1];

    return {
      x: ev.screenX,
      // handle's "left" style is relative to the grid column
      handle: {el: handleEl, left: parseFloat(handleEl.style.left)},
      leftCol: {idx: resizeColumnIndex, col: leftCol, minWidth: leftColMinWidth, deltaMin},
      rightCol: {
        idx: resizeColumnIndex + 1,
        col: rightCol,
        minWidth: rightCol ? this.getColumnMinWidth(rightCol) : 0,
      },
    };
  }

  getColumnMinWidth(col: TableColumn) {
    return col.resizeMinWidth ?? Math.min(col.initialWidth, DEFAULT_RESIZE_MIN_WIDTH);
  }

  setDefaultColumnWidths() {
    const columnWidthOverrides = {};
    this.update({columnWidthOverrides});
    let updatedColumns: TableColumn[] = this.state.columns.map((col) => {
      return {
        ...col,
        computedWidth: col.initialWidth,
      };
    });
    updatedColumns = this.expandColumnsToFillTable(updatedColumns);
    this.commitUpdatedColumnWidths(updatedColumns);
    this.dispatchEvent(new CustomEvent(`columnResize`, {detail: {columnWidthOverrides}}) as ColumnResizeEvent);
  }

  updateComputedColumnWidths(recalculate = false) {
    if (!this.state.columns.length || !this.state.scrollEl) {
      return;
    }

    const previouslyCalculatedWidths = Boolean(this.state.columns[0].computedWidth);
    const useComputedWidth = previouslyCalculatedWidths && !recalculate;
    let updatedColumns: TableColumn[] = this.state.columns.map((col, i) => {
      const widthOverride = this.state.columnWidthOverrides[i];
      return {
        ...col,
        computedWidth: Number.isFinite(widthOverride)
          ? widthOverride
          : useComputedWidth
            ? col.computedWidth
            : col.initialWidth,
      };
    });

    if (!useComputedWidth) {
      updatedColumns = this.expandColumnsToFillTable(updatedColumns);
    }
    this.commitUpdatedColumnWidths(updatedColumns);
  }

  commitUpdatedColumnWidths(updatedColumns: TableColumn[]) {
    const dataColumnWidthsMatrix: Array<{count: number; width: number}> = [];
    let prevWidth = null;
    const gapSize = this.helpers.getGapSize();
    let offsetLeft = 0;
    updatedColumns.forEach((column) => {
      column.offsetLeft = offsetLeft;
      offsetLeft += column.computedWidth + gapSize;
      // Populate our dataColumnWidthsMatrix which is used to determine the
      // width of the data columns in the CSS grid
      if (!column.sticky) {
        if (prevWidth !== column.computedWidth) {
          dataColumnWidthsMatrix.push({count: 0, width: column.computedWidth});
          prevWidth = column.computedWidth;
        }
        dataColumnWidthsMatrix[dataColumnWidthsMatrix.length - 1].count += 1;
      }
    });

    this.update({columns: updatedColumns, dataColumnWidthsMatrix});
    this.updateVirtualizedRenderState();
  }

  expandColumnsToFillTable(columns: TableColumn[]) {
    const totalColumnsWidth = getTotalColumnsWidth({columns, gapSize: this.helpers.getGapSize()});
    const tableWidth = this.helpers.getInternalTableWidth();
    if (totalColumnsWidth < tableWidth) {
      // If the columns don't span the width of the table, this block of code grows any columns that aren't
      // fixedWidth (and haven't been resized) to fit the width of the table.
      const resizedColIndices = getResizedColIndices(this.state.columnWidthOverrides);
      const summedNonFixedColumnWidth = columns
        .filter((col, index) => !col.fixedWidth && !resizedColIndices.includes(index))
        .reduce((accSum, col) => accSum + col.computedWidth, 0);
      const unallocatedWidth = tableWidth - totalColumnsWidth;
      const widthAdjustmentRatio = 1 + unallocatedWidth / summedNonFixedColumnWidth;
      columns = columns.map((col, index) => {
        const shouldResize = !col.fixedWidth && !resizedColIndices.includes(index);
        return {
          ...col,
          computedWidth: shouldResize ? Math.floor(col.computedWidth * widthAdjustmentRatio) : col.computedWidth,
        };
      });
    }
    return columns;
  }

  updateTableWidth(tableWidth: number) {
    this.update({tableWidth});
    this.updateComputedColumnWidths(true);
  }

  resetResizeState() {
    this.cleanResizeEventListeners();
    this.update({
      resizeColumnIndex: null,
    });
  }

  cleanResizeEventListeners() {
    window.removeEventListener(`blur`, this.onWindowBlur, true);
    window.removeEventListener(`mousemove`, this.onResizeMouseMove, true);
    window.removeEventListener(`mouseup`, this.onResizeMouseUp, true);
    this.onWindowBlur = null;
    this.onResizeMouseMove = null;
    this.onResizeMouseUp = null;
  }

  scrollElInserted(el: HTMLElement) {
    this.update({scrollEl: el});
    requestAnimationFrame(() => {
      if (this._deferredColumnWidthOverrides) {
        this.dispatchEvent(
          new CustomEvent(`columnResize`, {
            detail: {columnWidthOverrides: this._deferredColumnWidthOverrides},
          }) as ColumnResizeEvent,
        );
        this._deferredColumnWidthOverrides = null;
      }
      this.updateComputedColumnWidths(true);
      if (this.attr(`start-scrolled-right`)) {
        // Ensures the value is visible for dashboard cards
        requestAnimationFrame(() => {
          this.state.scrollEl.scrollLeft = Number.MAX_SAFE_INTEGER;
        });
      }
    });
  }

  scrollParentElInserted(el: HTMLElement) {
    this.update({scrollParentEl: el});
  }

  isRowVirtualHovered(context: CellRenderContext): boolean {
    if (!this.attr(`hovered-series-id`) || context.rowType !== RowType.Body) {
      return false;
    }
    return this.state.rows[context.rowIdx].identifier === this.attr(`hovered-series-id`);
  }

  isRowHovered(context: CellRenderContext) {
    if (Number.isFinite(this.state.resizeColumnIndex)) {
      // no hover effect when resizing columns
      return false;
    }

    const isHovered = isSameRowByContext(context, this.state.hoveredCellContext);
    return isHovered || this.isRowVirtualHovered(context);
  }

  isSelectedCell(context: BodyCellRenderContext) {
    const selectedCoords = this.attr(`selected-cell`);
    if (!selectedCoords || !context) {
      return false;
    }

    return (
      selectedCoords.columnIdx === context.columnIdx &&
      selectedCoords.rowIdx === context.rowIdx &&
      selectedCoords.groupedRowIdx === context.groupedRowIdx
    );
  }

  /** Check if segment block labels should be shown, i.e. there are multiple segments in grouped sort mode and
   * no search filter is active */
  showSegmentCellBlocks() {
    return this.segmentCellBlocksEnabled() && !this.state.searchFilter;
  }

  _emitSortChange() {
    this.dispatchEvent(
      new CustomEvent(`sortChange`, {
        detail: {
          sorting: this.state.sorting,
          rows: this.state.filteredRows,
        },
      }) as SortChangeEvent,
    );
  }

  updateFilteredRows() {
    let filteredRows: TableRow[];
    if (this.state.searchFilter) {
      const searchStr = this.state.searchFilter.trim().toLowerCase();
      filteredRows = this._getMatchingRows(searchStr);
    } else {
      filteredRows = this.state.rows;
    }

    filteredRows.forEach((row, rowIdx) => {
      row.filteredRowIdx = rowIdx;
      if (row.groupedRows) {
        row.groupedRows.forEach((groupedRow) => {
          groupedRow.filteredRowIdx = rowIdx;
        });
      }
    });

    this.update({filteredRows});
    this.updateFlatFilteredRows();
    this.updateVirtualizedRenderState();
  }

  private _getMatchingRows(searchStr: string) {
    const displayRows = [];
    // iteration is faster than filter + map
    const searchableCols = this.state.columns.filter(isSegmentColumn);
    for (const val of this.state.rows) {
      const matchParts = {};
      let score = 0;

      // try matching every searchable field
      for (const col of searchableCols) {
        const field = col.field;
        const targetStr = this.helpers.getCellLabel(val, col);
        const targetSanitizedStr = targetStr.toLowerCase();
        const match = fuzzyMatchSanitized(targetSanitizedStr, searchStr);

        if (match) {
          matchParts[field] = matchPartsFromSegments(targetStr, match.segments);
          score = Math.max(match.score, score);
        }
      }

      if (score) {
        displayRows.push(val);
      }
    }
    return displayRows;
  }

  updateFlatFilteredRows() {
    const hasSegmentCellBlocks = this.showSegmentCellBlocks();
    const theme = this.getTheme();
    const {multimetricStyles} = theme;
    const flatFilteredRows: TableRow[] = [];
    const gapSize = this.helpers.getGapSize();
    let offsetTop = 0;
    const rowHeightsMatrix: Array<{height: number; count: number}> = [];
    let prevRowHeight = null;

    /**
     * When the row height changes, start a new count for the new row height.
     * Increment it either way.
     */
    const updateRowHeightsMatrix = (row: TableRow): void => {
      if (prevRowHeight !== row.rowHeight) {
        rowHeightsMatrix.push({height: row.rowHeight, count: 0});
        prevRowHeight = row.rowHeight;
      }
      rowHeightsMatrix[rowHeightsMatrix.length - 1].count += 1;
    };

    this.state.filteredRows.forEach((row) => {
      row.segmentStartRow = hasSegmentCellBlocks && row.segmentBlockStarts?.size > 0;
      const segmentEndRow = hasSegmentCellBlocks && row.segmentBlockEnds?.size > 0;
      const hasExpandedGroupedRows = !row.isCollapsed && Boolean(row.groupedRows?.length);

      row.offsetTop = offsetTop;
      row.segmentEndRow = segmentEndRow && !hasExpandedGroupedRows;
      row.rowHeight =
        theme.rowHeight +
        (row.segmentStartRow && !multimetricStyles ? stylusConstants.SEGMENT_CELL_BLOCK_GAP_SIZE / 2 : 0) +
        (row.segmentEndRow && !multimetricStyles ? stylusConstants.SEGMENT_CELL_BLOCK_GAP_SIZE / 2 : 0);
      updateRowHeightsMatrix(row);

      offsetTop += row.rowHeight + gapSize;
      flatFilteredRows.push(row);

      if (hasExpandedGroupedRows) {
        row.segmentEndRow = false;
        row.groupedRows.forEach((groupedRow) => {
          groupedRow.offsetTop = offsetTop;
          groupedRow.segmentEndRow = segmentEndRow && groupedRow.groupedRowIdx === row.groupedRows.length - 1;
          groupedRow.rowHeight =
            theme.rowHeight + (groupedRow.segmentEndRow ? stylusConstants.SEGMENT_CELL_BLOCK_GAP_SIZE : 0);
          updateRowHeightsMatrix(groupedRow);
          flatFilteredRows.push(groupedRow);
          offsetTop += groupedRow.rowHeight + gapSize;
        });
      }
    });

    this.update({flatFilteredRows, rowHeightsMatrix});
  }

  getVirtualizedRenderState() {
    return pick(this.state, [
      `renderRowStartIdx`,
      `numRowsToRender`,
      `renderDataColumnStartIdx`,
      `numDataColumnsToRender`,
    ]);
  }

  /**
   * Recompute the virtualized rows/columns to render. For example, it'll run in the following cases:
   * - `rows` updated
   * - `columns` updated
   * - scroll (vertical/horizontal)
   * - browser is resized
   * - searchFilter is changed
   */
  updateVirtualizedRenderState() {
    const {scrollEl} = this.state;
    if (!scrollEl) {
      return;
    }

    if (!scrollEl.isConnected) {
      // The scrollEl hasn't been added to DOM yet, in which case clientHeight/clientWidth won't be correct.
      return;
    }

    const prevRenderState = this.getVirtualizedRenderState();
    const newRenderState = {...prevRenderState};

    const verticalScrollEl = this.helpers.getVerticalScrollEl();

    const scrollTop =
      verticalScrollEl !== scrollEl
        ? verticalScrollEl.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top
        : scrollEl.scrollTop;
    this.setVerticalScrolledClass(scrollTop);
    const headersHeight = sum(this.getHeaderRowHeights());

    const {flatFilteredRows} = this.state;

    // first row whose bottom edge is in the visible area
    const firstVisibleFlatRowIdx = sortedIndex(
      flatFilteredRows.map((row) => row.offsetTop + row.rowHeight),
      scrollTop,
    );
    const renderRowStartIdx = Math.max(0, firstVisibleFlatRowIdx - NUM_PADDING_ROWS_TO_RENDER);

    const scrollBottom = scrollTop + verticalScrollEl.clientHeight - headersHeight;
    // will be 1 larger the last row whose top edge is in the visible area
    const visibleFlatRowEndIdx = sortedLastIndex(
      flatFilteredRows.map((row) => row.offsetTop),
      scrollBottom,
    );
    const renderRowEndIdx = Math.min(flatFilteredRows.length, visibleFlatRowEndIdx + NUM_PADDING_ROWS_TO_RENDER);

    const numRowsToRender = renderRowEndIdx - renderRowStartIdx;
    Object.assign(newRenderState, {renderRowStartIdx, numRowsToRender});

    const dataColumns = this.getDataColumns();
    const {scrollLeft} = scrollEl;

    // If there's no sticky columns, then the first column's offsetLeft is 0.
    const firstDataColumnOffsetLeft =
      dataColumns.length && this.helpers.shouldUseStickyColumns() ? dataColumns[0].offsetLeft : 0;
    const scrollRight = scrollLeft + scrollEl.clientWidth - firstDataColumnOffsetLeft;

    // First column whose right edge is in the visible area
    const firstVisibleDataColumnIdx = sortedIndex(
      dataColumns.map((col) => col.offsetLeft + col.computedWidth - firstDataColumnOffsetLeft),
      scrollLeft,
    );
    const renderDataColumnStartIdx = Math.max(0, firstVisibleDataColumnIdx - NUM_PADDING_DATA_COLS_TO_RENDER);

    // will be 1 larger the last column whose right edge is in the visible area
    const visibleDataColumnEndIdx = sortedLastIndex(
      dataColumns.map((col) => col.offsetLeft - firstDataColumnOffsetLeft),
      scrollRight,
    );
    const renderDataColumnEndIdx = Math.min(
      dataColumns.length,
      visibleDataColumnEndIdx + NUM_PADDING_DATA_COLS_TO_RENDER,
    );

    const numDataColumnsToRender = renderDataColumnEndIdx - renderDataColumnStartIdx;
    Object.assign(newRenderState, {renderDataColumnStartIdx, numDataColumnsToRender});

    if (!isEqual(prevRenderState, newRenderState)) {
      this.update(newRenderState);
    }
  }

  updateVirtualizedRenderStateOnScroll() {
    this.updateVirtualizedRenderState();
  }

  /** Sets the 'vertical-scrolled' class as needed, used for adding/removing header shadow  */
  setVerticalScrolledClass(scrollTop: number) {
    const verticalScrolled = scrollTop !== 0;
    if (verticalScrolled && !this._verticalScrolled) {
      this._verticalScrolled = true;
      this.state.scrollEl?.classList?.add(`vertical-scrolled`);
    } else if (!verticalScrolled && this._verticalScrolled) {
      this._verticalScrolled = false;
      this.state.scrollEl?.classList?.remove(`vertical-scrolled`);
    }
  }

  getFlatRowsToRender(): TableRow[] {
    const {renderRowStartIdx, numRowsToRender} = this.state;
    const renderRowEndIdx = renderRowStartIdx + numRowsToRender;

    const rowsToRender = this.state.flatFilteredRows.slice(renderRowStartIdx, renderRowEndIdx);
    rowsToRender.forEach((row, renderRowIdx) => {
      row.renderRowIdx = renderRowIdx;
    });

    return rowsToRender;
  }

  getFixedColumns(): SegmentColumn[] {
    return this.state.columns.filter((col) => col.sticky) as SegmentColumn[];
  }

  getDataColumns(): DataColumn[] {
    return this.state.columns.filter((col) => !col.sticky) as DataColumn[];
  }

  /**Returns data columns while accounting for time comparison columns, returning only the Current column out of Current and Past.*/
  getCurrentDataColumns(): DataColumn[] {
    return this.getDataColumns().filter(
      (col) =>
        !col.timeComparisonMetadata ||
        col.timeComparisonMetadata.timeComparisonColumnType === TimeComparisonColumnType.Current,
    );
  }

  getDataColumnsToRender(): DataColumn[] {
    const {renderDataColumnStartIdx, numDataColumnsToRender} = this.state;
    const renderDataColumnEndIdx = renderDataColumnStartIdx + numDataColumnsToRender;
    return this.getDataColumns().slice(renderDataColumnStartIdx, renderDataColumnEndIdx);
  }
  /** All data column headers are rendered (rather than just virtualized columns).
   * This is to provide visual context to users as they scroll.*/
  getHeaderDataColumnsToRender() {
    return this.getDataColumns();
  }

  getTableEndRowIdx() {
    const numHeaderRows = this.getNumHeaderRows();
    const numMetricFooterRows = this.showMultimetricFooter() ? 1 : 0;
    return numHeaderRows + this.state.filteredRows.length + numMetricFooterRows;
  }

  getGroupHeadersToRender(): GroupHeader[] | null {
    return this.state.groupHeaders;
  }

  isSearchEnabled() {
    return !this.isBoardMode() && this.state.rows.length >= 2;
  }

  getPreTitleRowHeight() {
    const groupHeaders = this.getGroupHeadersToRender();
    return groupHeaders ? TITLE_ROW_HEIGHT : null;
  }

  getTimeComparisonHeaderRowHeight() {
    return this.hasTimeComparisonHeaders() ? TIME_COMPARISON_HEADER_ROW_HEIGHT : null;
  }

  getTitleRowHeight() {
    return this.attr(`is-pivot-view`) || this.canUseViewN() ? TITLE_ROW_WITH_SUBTITLE_HEIGHT : TITLE_ROW_HEIGHT;
  }

  getHeaderRowHeights() {
    const preTitleRowHeight = this.getPreTitleRowHeight();
    const timeComparisonHeaderRowHeight = this.getTimeComparisonHeaderRowHeight();
    return [
      ...(Number.isFinite(preTitleRowHeight) ? [preTitleRowHeight] : []),
      this.getTitleRowHeight(),
      ...(Number.isFinite(timeComparisonHeaderRowHeight) ? [timeComparisonHeaderRowHeight] : []),
    ];
  }

  getNumHeaderRows() {
    return this.getHeaderRowHeights().length;
  }

  hasTimeComparisonHeaders(): boolean {
    return this.state.columns.some((col) => isTimeComparisonColumnType(col));
  }

  private countStickyColumns(columns: TableColumn[]) {
    return columns.findIndex((col) => !col.sticky);
  }

  private _allFilteredRowsSelected(): boolean {
    const selectableFilteredRows = this.state.filteredRows.filter((row) => !row.isSelectDisabled);
    return selectableFilteredRows.every((row) => row.isSelected);
  }

  /** Check if segment block labels should be generated, i.e. there are multiple segments in grouped sort mode */
  private segmentCellBlocksEnabled() {
    const {columns, sorting} = this.state;
    return segmentCellBlocksEnabled({columns, sorting});
  }

  private updateSegmentCellBlocksAndRows(): void {
    const {rows} = this.state;
    if (!this.segmentCellBlocksEnabled() || !rows.length) {
      return;
    }

    const colOffset = this.state.columns.findIndex((col) => col.type === TableColumnType.Segment);
    const segmentCellBlocks = generateSegmentCellBlocks({
      rows,
      colOffset,
      isMultimetricMode: this.isMultimetricMode(),
      segmentMetadata: this.state.segmentMetadata,
      hasComparison: this.attr(`has-comparison`),
    });
    updateRowsWithSegmentBoundaries({rows, segmentCellBlocks});

    this.update({segmentCellBlocks, rows});
  }

  private cellPointerLeaveActions() {
    const prevContext = this.state.hoveredCellContext;
    this.update({
      hoveredCellContext: null,
      hoveredTruncatedTextType: null,
    });
    this.helpers.fireRowHoverEvent({context: null, prevContext});
  }

  canUseViewN() {
    return Boolean(this.state.sorting) && isPostViewNSortConfig(this.state.sorting);
  }

  canRenderViewNControl({segmentColumn}: {segmentColumn: SegmentColumn}): boolean {
    return this.canUseViewN() && (this.state.sorting.sortBy === SortBy.Column || segmentColumn.segmentIndex === 0);
  }

  hasColumnWithSubtitle(): boolean {
    return this.state.columns.some((column) => {
      return Boolean(column.subtitle) || (this.canUseViewN() && isSegmentColumn(column));
    });
  }

  handleViewNLimitInputFocus({segmentIdx}: {segmentIdx: number}) {
    this._viewNLimitInputBlurPromise = new Promise((resolve) => {
      this._resolveViewNLimitInputBlur = resolve;
    });
    if (!this.hasViewNLimit({segmentIdx})) {
      this.updateViewNLimit({
        segmentIdx,
        viewNLimit: this.getViewNLimitInputValue({segmentIdx}),
      });
    }
  }

  handleViewNLimitInputBlur() {
    this._resolveViewNLimitInputBlur();
  }

  handleViewNLimitInputChange({value, segmentIdx}: {value: number; segmentIdx: number}) {
    const newViewNLimit = parseNumber(value);
    const isValidViewNLimit = Number.isInteger(newViewNLimit) && newViewNLimit >= 1;
    this.updateViewNLimit({
      segmentIdx,
      viewNLimit: isValidViewNLimit ? newViewNLimit : VIEW_N_DEFAULT_LIMIT,
    });
  }

  trackViewNChangeOnMenuClose({segmentIdx}: {segmentIdx: number}) {
    const oldSortConfig = this._oldViewNSortConfig;
    const newSortConfig = this.state.sorting;
    if (isEqual(oldSortConfig, newSortConfig)) {
      return;
    }

    this._oldViewNSortConfig = null;
    this._viewNLimitInputBlurPromise = null;
    this._resolveViewNLimitInputBlur = null;

    const oldViewNLimit = getViewNLimit({sortConfig: oldSortConfig, segmentIdx});
    const newViewNLimit = getViewNLimit({sortConfig: newSortConfig, segmentIdx});
    let changeType: string;
    const oldHasViewNLimit = Number.isFinite(oldViewNLimit);
    const hasViewNLimit = Number.isFinite(newViewNLimit);
    if (oldHasViewNLimit && hasViewNLimit && oldViewNLimit !== newViewNLimit) {
      changeType = `view_n_limit`;
    } else if (oldHasViewNLimit && !hasViewNLimit) {
      changeType = `enable_show_all`;
    } else if (!oldViewNLimit && hasViewNLimit) {
      changeType = `enable_view_n`;
    } else {
      changeType = `unknown`;
    }
    emitTrackEvent(this, {
      eventName: `View N Change`,
      eventPrefix: `Chart Controls`,
      props: {
        tab: getReportName(),
        'Change Type': changeType,
        'Old View N Limit': oldViewNLimit,
        'New View N Limit': newViewNLimit,
        // TODO(mack): Right now this is tracking table-specific names. Should switching to tracking "Chart type"
        // in the future
        'Chart Widget Type': this.attr(`track-name`),
        '# of Segment Columns': newSortConfig.colSortAttrs.length,
        '# of Rows': this.state.rows.length,
        'Sort Type': newSortConfig.sortBy === SortBy.Value ? `flattened` : `grouped`,
        'Segment Column Index': segmentIdx,
        'Segment Cardinality': this.getSegmentColumnFromIndex(segmentIdx).segmentCardinality,
        'Old Sort Config': oldSortConfig,
        'New Sort Config': newSortConfig,
      },
    });
  }

  handleViewNMenuChange({ev, segmentIdx}: {ev: MenuChangeEvent; segmentIdx: number}) {
    if (ev.detail.isOpen) {
      this._oldViewNSortConfig = this.state.sorting;
    } else {
      if (this._viewNLimitInputBlurPromise) {
        // If the input was focused when the menu was closed, the menuChange event will fire before the input's blur
        // event. But need to wait for blur event (via promise) to update View N state before tracking the new View N
        // state.
        this._viewNLimitInputBlurPromise.then(() => this.trackViewNChangeOnMenuClose({segmentIdx}));
      } else {
        this.trackViewNChangeOnMenuClose({segmentIdx});
      }
    }
  }

  getSegmentColumnFromIndex(segmentIdx: number): SegmentColumn {
    return this.state.columns.find(
      (column) => isSegmentColumn(column) && column.segmentIndex === segmentIdx,
    ) as SegmentColumn;
  }

  getViewNSortInfo({segmentIdx}: {segmentIdx: number}): {sortColumn: TableColumn; sortOrder: SortOrder} {
    const {sorting} = this.state;
    if (sorting.sortBy === SortBy.Value) {
      const column = this.state.columns.find((col) => col.field === sorting.valueField);
      return {sortColumn: column, sortOrder: sorting.sortOrder};
    } else {
      const flatSortConfig = sorting.colSortAttrs[segmentIdx];
      return {
        sortColumn:
          flatSortConfig.sortBy === SortBy.Label
            ? this.getSegmentColumnFromIndex(segmentIdx)
            : this.state.columns.find((column) => column.field === flatSortConfig.valueField),
        sortOrder: flatSortConfig.sortOrder,
      };
    }
  }

  getViewNSortOrderLabel({segmentIdx}: {segmentIdx: number}) {
    const {sortColumn, sortOrder} = this.getViewNSortInfo({segmentIdx});
    if (isSegmentColumn(sortColumn)) {
      return sortOrder === SortOrder.Ascending ? `First` : `Last`;
    } else {
      return sortOrder === SortOrder.Ascending ? `Bottom` : `Top`;
    }
  }

  getViewNLabel({segmentIdx}: {segmentIdx: number}) {
    let label: string;
    if (!this.hasViewNLimit({segmentIdx})) {
      label = `Showing all`;
    } else {
      const {sortColumn} = this.getViewNSortInfo({segmentIdx});
      const sortOrderLabel = this.getViewNSortOrderLabel({segmentIdx});
      const viewNLimit = this.getViewNLimit({segmentIdx});
      label = `${sortOrderLabel} ${viewNLimit}`;
      if (!isSegmentColumn(sortColumn) && sortColumn.titlePrefix) {
        label += ` • ${sortColumn.titlePrefix}`;
      } else if (
        this.state.sorting.sortBy === SortBy.Value &&
        segmentIdx === 0 &&
        getNumSegments(this.state.columns) > 1
      ) {
        label += ` • ${sortColumn.title}`;
      }
    }

    return label;
  }

  getViewNLimitInputValue({segmentIdx}: {segmentIdx: number}): number {
    const viewNLimit = this.getViewNLimit({segmentIdx});
    return !Number.isFinite(viewNLimit) ? VIEW_N_DEFAULT_LIMIT : viewNLimit;
  }

  handleViewNShowAllClick({segmentIdx}: {segmentIdx: number}) {
    const newViewNLimit = this.hasViewNLimit({segmentIdx}) ? null : VIEW_N_DEFAULT_LIMIT;
    this.updateViewNLimit({segmentIdx, viewNLimit: newViewNLimit});
  }

  getViewNLimit({segmentIdx}: {segmentIdx: number}) {
    return getViewNLimit({sortConfig: this.state.sorting, segmentIdx});
  }

  hasViewNLimit({segmentIdx}: {segmentIdx: number}) {
    return Number.isFinite(this.getViewNLimit({segmentIdx}));
  }

  updateViewNLimit({segmentIdx, viewNLimit}: {segmentIdx: number; viewNLimit: number | null}) {
    this.update({
      sorting: produce(this.state.sorting, (sorting) => {
        if (sorting.sortBy === SortBy.Value) {
          sorting.viewNLimit = viewNLimit;
        } else {
          sorting.colSortAttrs[segmentIdx].viewNLimit = viewNLimit;
        }
      }),
    });

    this._emitSortChange();
  }

  isBoardMode() {
    return !this.attr(`resizing-layout`);
  }
}

export const DataTableEl = defineAndMakeJsxFactory<DataTableJsxProps>(COMPONENT_NAME, DataTable);
export const DataTableReact = panel2react<DataTableJsxProps>(COMPONENT_NAME, DataTable);
